<template>
    <div class="verification-code">
        <div class="verification-code__inputs">
            <input
                v-for="(digit, index) in digits"
                :key="index"
                :ref="`digit${index}`"
                :value="digit"
                type="text"
                inputmode="text"
                autocomplete="one-time-code"
                maxlength="1"
                class="verification-code__box"
                :class="{
                    'verification-code__box--filled': Boolean(digit),
                    'verification-code__box--error': hasError,
                }"
                :disabled="isDisabled"
                :autofocus="hasAutofocus && index === 0"
                @input="onInput(index, $event)"
                @keydown="onKeyDown(index, $event)"
                @paste.prevent="onPaste"
                @focus="onFocus($event)"
            />
        </div>

        <div class="verification-code__progress" aria-hidden="true">
            <span
                v-for="(digit, index) in digits"
                :key="`dot-${index}`"
                class="verification-code__dot"
                :class="{ 'verification-code__dot--filled': Boolean(digit) }"
            ></span>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            length: { type: Number, default: 6 },
            hasError: { type: Boolean, default: false },
            isDisabled: { type: Boolean, default: false },
            hasAutofocus: { type: Boolean, default: true },
        },

        data() {
            return {
                digits: Array(this.length).fill(''),
            }
        },

        methods: {
            focusAt(index) {
                const ref = this.$refs[`digit${index}`]
                const input = Array.isArray(ref) ? ref[0] : ref
                if (input) input.focus()
            },

            emitCode() {
                this.$emit('change', this.digits.join(''))
            },

            setDigit(index, value) {
                const char = (value || '').toUpperCase()
                if (char && !/^[A-Z0-9]$/.test(char)) return false
                this.$set(this.digits, index, char)
                this.emitCode()
                return Boolean(char)
            },

            onFocus(event) {
                event.target.select()
            },

            onInput(index, event) {
                const char = (event.target.value || '').toUpperCase().slice(-1)
                if (!this.setDigit(index, char)) {
                    event.target.value = this.digits[index]
                    return
                }
                if (char && index < this.length - 1) this.focusAt(index + 1)
            },

            onKeyDown(index, event) {
                if (event.key === 'Backspace') {
                    if (this.digits[index]) {
                        this.setDigit(index, '')
                        return
                    }

                    if (index > 0) {
                        event.preventDefault()
                        this.setDigit(index - 1, '')
                        this.focusAt(index - 1)
                    }
                    return
                }

                if (event.key === 'ArrowLeft' && index > 0) {
                    event.preventDefault()
                    this.focusAt(index - 1)
                } else if (event.key === 'ArrowRight' && index < this.length - 1) {
                    event.preventDefault()
                    this.focusAt(index + 1)
                }
            },

            onPaste(event) {
                const pasted = (event.clipboardData.getData('text') || '')
                    .replace(/[^a-zA-Z0-9]/g, '')
                    .slice(0, this.length)
                    .toUpperCase()

                if (!pasted) return

                pasted.split('').forEach((char, index) => {
                    this.$set(this.digits, index, char)
                })
                this.emitCode()

                this.$nextTick(() => {
                    this.focusAt(Math.min(pasted.length, this.length - 1))
                })
            },
        },

        mounted() {
            if (this.hasAutofocus) {
                this.$nextTick(() => this.focusAt(0))
            }
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .verification-code {
        &__inputs {
            display: flex;
            justify-content: space-between;
            gap: rem(8px);
        }

        &__box {
            flex: 1 1 0;
            min-width: 0;
            max-width: rem(56px);
            height: rem(56px);
            padding: 0;
            text-align: center;
            font-family: $font-family--base;
            font-size: rem(22px);
            font-weight: 600;
            line-height: rem(56px);
            color: $color--txt-header;
            background: $color-white;
            border: 1px solid $color--border-input;
            border-radius: rem(12px);
            outline: none;
            caret-color: $color--accent-1;
            transition: border-color $duration--slow ease, box-shadow $duration--slow ease;

            &:focus {
                border-color: $color--accent-1;
                box-shadow: 0 0 0 rem(3px) rgba($color--accent-1, 0.16);
            }

            &--filled {
                border-color: $color--accent-1;
            }

            &--error {
                border-color: $color--accent-4;
            }

            &:disabled {
                opacity: $opacity--is-disabled;
            }
        }

        &__progress {
            display: flex;
            justify-content: center;
            gap: rem(8px);
            margin-top: rem(14px);
        }

        &__dot {
            width: rem(6px);
            height: rem(6px);
            border-radius: 50%;
            background: $color--border-input;
            transition: background $duration--slow ease, transform $duration--slow ease;

            &--filled {
                background: $color--accent-1;
                transform: scale(1.15);
            }
        }
    }
</style>
